import {
  calculateItemTotal,
  getSellingPriceWithCurrency,
  getVariantFromProduct,
  tagsFromVariant,
} from '../../lib/utils'
import { useCallback, useContext, useEffect, useState } from 'react'
import { CartContext } from '../../lib/contexts/cart'
import { CheckoutContext } from '../../lib/contexts/checkout'
import Chips from '../../components/Chips'
import { ClientContext } from '../../lib/contexts/client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'next/link'
import PlacePicker from '../../components/PlacePicker'
import classNames from 'classnames'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { getInventory } from '../../lib/api'

export default function DeliveryForm() {
  const {
    detail,
    setDeliveryType,
    setDeliveryAddress,
    setItems: setCheckoutItems,
  } = useContext(CheckoutContext)

  const { config, shop } = useContext(ClientContext)

  const cart = useContext(CartContext)

  const [inventory, setInventory] = useState([])

  const isCheckout = detail.checkoutPermissions

  function canIncrease(product, variant, count) {
    if (!product.limitedStock) {
      return true
    }

    const entry = cart.contains(product, variant)
    const position = cart.items.indexOf(entry)

    const productStock = inventory[position]

    if (!productStock) {
      return false
    }

    return productStock.stock >= count
  }

  // count is string
  function changeCount(product, variant, count) {
    const c = parseInt(count) || 0

    if (c >= 0 && canIncrease(product, variant, c)) {
      cart.changeCount(product, variant, c)
    }
  }

  const updateInventory = useCallback(
    async function _updateInventory() {
      const inventory = (
        await Promise.all(
          cart.items.map((item) => {
            return getInventory({ product: item.product._id, variant: item.variant })
          })
        )
      ).map((inventory) => inventory[0])

      setInventory(inventory)
    },
    [cart.items]
  )

  useEffect(() => {
    updateInventory()
  }, [updateInventory])

  useEffect(
    function updateCheckout() {
      setCheckoutItems(cart.items)
    },
    [cart.items, setCheckoutItems]
  )

  useEffect(
    function setDefaultDeliveryOption() {
      if (config.localDelivery) {
        setDeliveryType('dispatch')
      }
    },
    [config.localDelivery, setDeliveryType]
  )

  useEffect(() => {
    !isCheckout && setCheckoutItems(cart.items)
  }, [cart.items, isCheckout, setCheckoutItems])

  //

  const { items } = detail

  let persistentMarker
  if (detail.delivery.rate?.option === 'pickup') {
    persistentMarker = {
      coordinate: [
        detail.delivery.rate.pickup.coordinate[0],
        detail.delivery.rate.pickup.coordinate[1],
      ],
      label: 'Pick up point',
    }
  }

  return (
    <>
      <p className="h6">Items ({items.length})</p>

      {items.length === 0 && (
        <p className="alert alert-primary">
          You have not added any items to your cart. Go to{' '}
          <Link href="/products">
            <a className="text-decoration-underline">all products</a>
          </Link>
        </p>
      )}

      <div>
        {items.map((item, index) => {
          const { product, variant, count } = item

          return (
            <div className="d-flex py-2 border-bottom" key={index}>
              <div>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt={product.title}
                  className="me-3 rounded"
                  style={{ height: 80, width: 80 }}
                  src={product.images[0].thumbnail}
                />
              </div>

              <div className="row flex-fill">
                <div className="col-lg-6 col-12">
                  <p className="mb-0 fw-bold">
                    <Link href={`/products/${product._id}/`}>
                      <a>{product.title}</a>
                    </Link>
                  </p>
                  {Boolean(variant) && (
                    <div className="my-2">
                      <Chips tags={tagsFromVariant(getVariantFromProduct(product, variant))} />
                    </div>
                  )}
                  {!isCheckout && (
                    <a
                      onClick={() => cart.removeItem(product, variant)}
                      className="text-danger cursor-pointer d-none d-lg-block"
                    >
                      Remove item
                    </a>
                  )}
                </div>

                {isCheckout ? (
                  <div className="col-lg-6 col-12">
                    <p>
                      {getSellingPriceWithCurrency(product)} <span className="text-muted">x</span>{' '}
                      {count}
                    </p>
                  </div>
                ) : (
                  <div className="col-lg-6 col-12">
                    <button
                      className={classNames(
                        'bg-white rounded-circle py-1 px-2',
                        ' border d-inline-flex align-items-center ',
                        'justify-content-center hover-light ',
                        'cursor-pointer user-select-none'
                      )}
                      style={{ height: 30, width: 30 }}
                      disabled={count <= 1}
                      onClick={() => changeCount(product, variant, count - 1)}
                    >
                      -
                    </button>

                    <input
                      type="number"
                      className="form-control d-inline mx-1"
                      min={1}
                      style={{ width: 80 }}
                      value={count}
                      onChange={(v) => changeCount(product, variant, v.target.value)}
                    />

                    <button
                      className={classNames(
                        'bg-white rounded-circle py-1 px-2',
                        ' border d-inline-flex align-items-center ',
                        'justify-content-center hover-light ',
                        'cursor-pointer user-select-none'
                      )}
                      disabled={!canIncrease(product, variant, count + 1)}
                      style={{ height: 30, width: 30 }}
                      onClick={() => changeCount(product, variant, count + 1)}
                    >
                      +
                    </button>
                  </div>
                )}
              </div>

              <div style={{ minWidth: 90 }} className="text-end">
                <strong>{calculateItemTotal(product, count)}</strong>

                {!isCheckout && (
                  <div className="py-3 d-block d-lg-none">
                    <a
                      onClick={() => cart.removeItem(product, variant)}
                      className="text-danger cursor-pointer"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </a>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <p className="h6 mt-5">Delivery</p>

      <div className="row mb-3">
        <div className="col-lg-6">
          <div className="form-floating">
            <select
              className="form-select"
              disabled={detail.checkoutPermissions?.delivery === 'view'}
              id="delivery-select"
              name="delivery.type"
              onChange={(v) => setDeliveryType(v.target.value)}
              value={detail.delivery.type}
            >
              {config.localDelivery && <option value="dispatch">Deliver to Location</option>}
              {config.allowPickups && <option value="pick-up">Pickup from Shop</option>}
              {!(config.localDelivery || config.allowPickups) && (
                <option disabled>No delivery options available</option>
              )}
            </select>
            <label htmlFor="delivery-select">Delivery Method</label>
          </div>
        </div>
      </div>

      {detail.delivery.type === 'pick-up' ? (
        shop.address && (
          <>
            <p className="text-muted mb-2">Shop Address</p>
            <p>
              {shop.address.street} <br />
              {shop.address.region}
            </p>

            <p>
              <a
                className="btn btn-secondary text-white"
                target="_blank"
                href={`https://www.google.com/maps/dir/?api=1&destination=${shop.address.gps.coordinates[0]},${shop.address.gps.coordinates[1]}`}
                rel="noreferrer"
              >
                View Directions
              </a>
            </p>
          </>
        )
      ) : (
        <>
          <p className="h6">Select delivery location</p>
          <div className="d-lg-block d-none">
            {detail.delivery.type === 'pickup' && (
              <div className="alert alert-secondary p-3">
                <p>
                  Dispatch is not available to your location. You need to pickup from the marked{' '}
                  <strong>pick up point</strong>.
                </p>

                <a
                  className="btn btn-outline-primary"
                  target="_blank"
                  href={
                    detail.delivery.rate
                      ? `https://www.google.com/maps/dir/?api=1&destination=${detail.delivery.rate.pickup.coordinate[0]},${detail.delivery.rate.pickup.coordinate[1]}`
                      : '#'
                  }
                  rel="noreferrer"
                >
                  View Pick up on map
                </a>
              </div>
            )}
          </div>

          <PlacePicker
            displayOnly={isCheckout && detail.checkoutPermissions?.delivery === 'view'}
            disableGesture={isCheckout && detail.checkoutPermissions?.delivery === 'view'}
            onSelectAddress={setDeliveryAddress}
            label="Your address"
            address={detail.delivery.address || shop.address}
            persistentMarker={persistentMarker}
          />

          <div className="d-lg-none d-block">
            {detail.delivery.type === 'pickup' && (
              <div className="alert alert-secondary p-3">
                <p>
                  Dispatch is not available to your location. You need to pickup from the marked{' '}
                  <strong>pick up point</strong>.
                </p>

                <a
                  className="btn btn-outline-primary"
                  target="_blank"
                  href={
                    detail.delivery.rate
                      ? `https://www.google.com/maps/dir/?api=1&destination=${detail.delivery.rate.pickup.coordinate[0]},${detail.delivery.rate.pickup.coordinate[1]}`
                      : ''
                  }
                  rel="noreferrer"
                >
                  View Pick up on map
                </a>
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
